/**
 * components/AISummaryBar.js — Phase 3
 *
 * Replaces raw WebSocket with the useStomp hook (@stomp/stompjs).
 * Visual design is unchanged from Phase 2.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Chip, Skeleton, Paper, Tooltip, IconButton } from '@mui/material';
import { AutoAwesome, WifiOff, Circle } from '@mui/icons-material';
import { aiApi } from '../services/hrmsApi';
import { useStomp } from '../hooks/useStomp';

const STATUS_COLORS = {
  connected:    '#4CAF50',
  connecting:   '#FFC107',
  disconnected: '#9E9E9E',
  error:        '#EF5350',
};

export default function AISummaryBar() {
  const [displayText, setDisplayText] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [isTyping,    setIsTyping]    = useState(false);
  const typeTimerRef  = useRef(null);

  // ── Typewriter animation ────────────────────────────────────────────────
  const animateText = useCallback((fullText) => {
    if (typeTimerRef.current) clearInterval(typeTimerRef.current);
    setIsTyping(true);
    let i = 0;
    setDisplayText('');
    typeTimerRef.current = setInterval(() => {
      i++;
      setDisplayText(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(typeTimerRef.current);
        setIsTyping(false);
      }
    }, 16);
  }, []);

  useEffect(() => () => { if (typeTimerRef.current) clearInterval(typeTimerRef.current); }, []);

  // ── REST initial load ────────────────────────────────────────────────────
  useEffect(() => {
    aiApi.getSummary()
      .then((res) => { setLoading(false); animateText(res.text); })
      .catch(() => {
        setLoading(false);
        setDisplayText('AI summary service initialising. Live metrics will appear shortly.');
      });
  }, [animateText]);

  // ── STOMP live updates via @stomp/stompjs ────────────────────────────────
  const handleMessage = useCallback((destination, body) => {
    if (destination === '/topic/ai-summary' && body?.text) {
      animateText(body.text);
    }
  }, [animateText]);

  const { status } = useStomp({
    endpoint:  '/ws',
    topics:    ['/topic/ai-summary'],
    onMessage: handleMessage,
  });

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #0A1628 0%, #1565C0 100%)',
        borderRadius: 2, p: 2, mb: 2,
        display: 'flex', alignItems: 'flex-start', gap: 2,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Decorative glow */}
      <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(0,188,212,0.15)', filter: 'blur(40px)', pointerEvents: 'none' }} />

      {/* Icon */}
      <Box sx={{ minWidth: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <AutoAwesome sx={{ color: '#FFD54F', fontSize: 22 }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Chip label="AI Summary" size="small" sx={{ bgcolor: 'rgba(255,213,79,0.2)', color: '#FFD54F', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
          <Chip label="Live" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.25)', color: '#81C784', fontWeight: 700, fontSize: '0.68rem', height: 20, '& .MuiChip-label': { px: 1 } }} />
        </Box>
        {loading ? (
          <>
            <Skeleton variant="text" width="90%" sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 0.5 }} />
            <Skeleton variant="text" width="75%" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)', lineHeight: 1.6, fontSize: '1.05rem' }}>
            {displayText}
            {isTyping && (
              <Box component="span" sx={{ display: 'inline-block', width: '2px', height: '1em', bgcolor: '#00BCD4', ml: '2px', verticalAlign: 'text-bottom', animation: 'blink 1s step-end infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } } }} />
            )}
          </Typography>
        )}
      </Box>

      {/* WebSocket status indicator */}
      <Tooltip title={`WebSocket: ${status}`} placement="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {status === 'error' || status === 'disconnected'
            ? <WifiOff sx={{ fontSize: 16, color: STATUS_COLORS[status] }} />
            : <Circle sx={{ fontSize: 10, color: STATUS_COLORS[status] }} />
          }
        </Box>
      </Tooltip>
    </Paper>
  );
}
